import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { MAX_PROFILES, PROFILE_AVATAR_COLORS, type Profile } from '@gobingebox/shared';
import { Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ProfileAvatar } from '@/components/ProfileAvatar';
import { profilesApi } from './api';
import { useAuth } from '@/features/auth/AuthContext';

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PROFILE_AVATAR_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          style={{ backgroundColor: c }}
          className={cn(
            'h-7 w-7 rounded-full ring-2 ring-offset-2 ring-offset-surface transition',
            value === c ? 'ring-ink' : 'ring-transparent',
          )}
          aria-label={`Pick color ${c}`}
        />
      ))}
    </div>
  );
}

function KidsToggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-ink-muted">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-brand" />
      Kids profile (limits mature content)
    </label>
  );
}

function ProfileEditor({ profile }: { profile: Profile }) {
  const { refresh, profiles } = useAuth();
  const [name, setName] = useState(profile.name);
  const [color, setColor] = useState<string>(profile.avatarColor);
  const [isKids, setIsKids] = useState(profile.isKids);

  const save = useMutation({
    mutationFn: () => profilesApi.update(profile.id, { name: name.trim(), avatarColor: color, isKids }),
    onSuccess: () => refresh(),
  });
  const remove = useMutation({ mutationFn: () => profilesApi.remove(profile.id), onSuccess: () => refresh() });

  const dirty = name.trim() !== profile.name || color !== profile.avatarColor || isKids !== profile.isKids;

  return (
    <div className="card p-4">
      <div className="flex items-start gap-4">
        <ProfileAvatar profile={{ name: name || '?', avatarColor: color, isKids }} size={56} />
        <div className="flex-1 space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} aria-label="Profile name" />
          <ColorPicker value={color} onChange={setColor} />
          <KidsToggle checked={isKids} onChange={setIsKids} />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => save.mutate()} disabled={!dirty || !name.trim() || save.isPending}>
              {!dirty && save.isSuccess ? 'Saved' : save.isPending ? 'Saving…' : 'Save'}
            </Button>
            {profiles.length > 1 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  if (window.confirm(`Delete "${profile.name}"? Its list and history will be removed.`)) remove.mutate();
                }}
                disabled={remove.isPending}
              >
                <Trash2 size={14} /> Delete
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProfilesManager() {
  const { profiles, refresh } = useAuth();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(PROFILE_AVATAR_COLORS[0]);
  const [isKids, setIsKids] = useState(false);

  const create = useMutation({
    mutationFn: () => profilesApi.create({ name: name.trim(), avatarColor: color, isKids }),
    onSuccess: () => {
      void refresh();
      setAdding(false);
      setName('');
      setIsKids(false);
      setColor(PROFILE_AVATAR_COLORS[0]);
    },
  });

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-muted">
        Each profile keeps its own My List and watch history. Everyone shares this account's plan.
      </p>
      {profiles.map((p) => (
        <ProfileEditor key={p.id} profile={p} />
      ))}

      {adding ? (
        <div className="card space-y-3 p-4">
          <Input placeholder="Profile name" value={name} onChange={(e) => setName(e.target.value)} maxLength={30} autoFocus />
          <ColorPicker value={color} onChange={setColor} />
          <KidsToggle checked={isKids} onChange={setIsKids} />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}>
              Create profile
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : profiles.length < MAX_PROFILES ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border py-4 text-sm font-medium text-ink-muted transition hover:border-brand hover:text-ink"
        >
          <Plus size={16} /> Add profile
        </button>
      ) : (
        <p className="text-xs text-ink-faint">Maximum of {MAX_PROFILES} profiles reached.</p>
      )}
    </div>
  );
}
